import { X } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useT } from '@/i18n';
import { cn } from '@/lib/cn';
import { Button, type ButtonVariant, IconButton } from './Button';
import { Field, Textarea } from './form';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** "drawer" slides in from the inline end, full height. */
  variant?: 'center' | 'drawer';
  /** Block closing (e.g. while saving). */
  dismissible?: boolean;
}

const WIDTHS = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' } as const;

/**
 * Native <dialog>: focus trap, Escape, inert background and top layer for
 * free. Content mounts only while open, so each opening starts fresh (and
 * finance forms mint a new Idempotency-Key).
 */
export function Modal({ open, ...props }: ModalProps) {
  if (!open) return null;
  return <OpenModal {...props} />;
}

function OpenModal({ onClose, title, description, children, footer, size = 'md', variant = 'center', dismissible = true }: Omit<ModalProps, 'open'>) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal?.();
    return () => dialog?.close?.();
  }, []);

  const drawer = variant === 'drawer';
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onMouseDown={(e) => {
        // A press on the backdrop targets the <dialog> itself.
        if (e.target === e.currentTarget && dismissible) onClose();
      }}
      className={cn(
        'w-full overflow-visible',
        drawer
          ? cn('ms-auto me-0 my-0 h-dvh max-h-dvh', WIDTHS[size === 'sm' ? 'md' : size])
          : cn('m-auto max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)]', WIDTHS[size]),
      )}
    >
      <div
        className={cn(
          'flex flex-col bg-surface',
          drawer ? 'h-full' : 'max-h-[calc(100dvh-2rem)] rounded-xl',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line-soft px-5 py-3.5">
          <div className="flex min-w-0 flex-col gap-0.5 pt-2">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="text-sm text-subtle">
                {description}
              </p>
            ) : null}
          </div>
          <IconButton icon={X} label={t('common.close')} onClick={onClose} disabled={!dismissible} className="-me-2" />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer ? <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line-soft bg-raised px-5 py-3">{footer}</footer> : null}
      </div>
    </dialog>
  );
}

/** A modal whose body is a form; the footer buttons submit it. */
export function FormModal({
  onSubmit,
  submitLabel,
  submitVariant = 'primary',
  pending,
  submitDisabled,
  children,
  extraFooter,
  ...props
}: Omit<ModalProps, 'footer'> & {
  onSubmit: () => void;
  submitLabel: ReactNode;
  submitVariant?: ButtonVariant;
  pending?: boolean;
  submitDisabled?: boolean;
  extraFooter?: ReactNode;
}) {
  const t = useT();
  const formId = useId();
  const handle = (e: FormEvent) => {
    e.preventDefault();
    if (!pending) onSubmit();
  };
  return (
    <Modal
      {...props}
      dismissible={!pending}
      footer={
        <>
          {extraFooter ? <div className="me-auto">{extraFooter}</div> : null}
          <Button onClick={props.onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} variant={submitVariant} loading={pending} disabled={submitDisabled}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handle} noValidate className="flex flex-col gap-4">
        {children}
      </form>
    </Modal>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel: ReactNode;
  tone?: 'primary' | 'danger' | 'accent';
  /** Ask for a note/reason; `true` makes it required. */
  input?: { label: ReactNode; required?: boolean; placeholder?: string };
  pending?: boolean;
  onConfirm: (text: string) => void;
  children?: ReactNode;
}

export function ConfirmDialog({ open, ...props }: ConfirmDialogProps) {
  if (!open) return null;
  return <ConfirmBody {...props} />;
}

function ConfirmBody({ onClose, title, message, confirmLabel, tone = 'primary', input, pending, onConfirm, children }: Omit<ConfirmDialogProps, 'open'>) {
  const t = useT();
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const missing = !!input?.required && !text.trim();
  return (
    <FormModal
      open
      size="sm"
      onClose={onClose}
      title={title}
      pending={pending}
      submitLabel={confirmLabel}
      submitVariant={tone}
      onSubmit={() => {
        setTouched(true);
        if (!missing) onConfirm(text.trim());
      }}
    >
      {message ? <div className="text-base leading-relaxed text-muted">{message}</div> : null}
      {children}
      {input ? (
        <Field label={input.label} required={input.required} optional={!input.required} error={touched && missing ? t('validation.required') : undefined}>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={input.placeholder} maxLength={300} autoFocus />
        </Field>
      ) : null}
    </FormModal>
  );
}
